import { get, camelCase } from 'lodash';
import { useMutation } from 'react-query';
import React, { useState, useContext } from 'react';
import { useDebouncedCallback } from 'beautiful-react-hooks';

import { Request } from '../Records/index';
import RespondentContext from '../Context/Respondent';
import { LinkRequestType } from '~/types/models/testimonial';
import { getLinkIconUrl } from '~/App/Modules/Testimonials/Components/Respondents/Response/ResponseLabel';
import {
  saveAttachment,
  SaveAttachmentParams,
  updateAttachment,
  UpdateAttachmentParams,
} from '../Api/TestimonialRespondent';
import { useTranslation } from 'react-i18next';
import { Input } from '@vimily/bonjoro-ui-components-v2/Input';
import cx from 'classnames';

function getRequestLinkPlaceholder(linkRequest: Request) {
  const { t } = useTranslation();
  const type = linkRequest.get('type');
  switch (type) {
    case LinkRequestType.Facebook:
    case LinkRequestType.Twitter:
    case LinkRequestType.Website:
      return t('testimonial:request.link.' + camelCase(type));
    case LinkRequestType.LinkedIn:
      return t('testimonial:request.link.linkedin');
    default:
      return linkRequest.get('label', 'Custom Link');
  }
}

export interface RequestLinksItemProps {
  request: Request;
}
export default function RequestLinksItem({ request }: RequestLinksItemProps) {
  const { respondent } = useContext(RespondentContext);
  const response = request.get('response');
  const linkIconUrl = getLinkIconUrl(request.get('type'));
  const [link, setLink] = useState<string>(get(response, 'text_response'));

  const { mutateAsync: saveAttachmentMutation } = useMutation(
    (saveAttachmentParams: SaveAttachmentParams) =>
      saveAttachment(saveAttachmentParams)
  );
  const { mutateAsync: updateAttachmentMutation } = useMutation(
    (updateAttachmentParams: UpdateAttachmentParams) =>
      updateAttachment(updateAttachmentParams)
  );

  const handleLinkChange = useDebouncedCallback(
    (value: string) => {
      setLink(value);
      const requestResponseId = get(request.get('response'), 'uuid');
      const respondentUuid = respondent.get('uuid');

      if (!requestResponseId) {
        saveAttachmentMutation({
          respondentId: respondentUuid,
          attachmentId: request.get('uuid'),
          textResponse: value,
        });
      } else {
        updateAttachmentMutation({
          respondentId: respondentUuid,
          requestResponseId,
          textResponse: value,
        });
      }
    },
    [respondent, request],
    600
  );

  return (
    <div className='flex flex-col items-start'>
      <div className={cx([
        'mb-3 px-2 flex w-full justify-between bg-white text-neutral-700 items-center',
        'v2-border v2-border-solid v2-border-neutral-300'
      ])}>
        <img src={linkIconUrl} className='rounded h-5 w-5' />
        <Input
          type='text'
          defaultValue={link}
          onChange={(evt: React.FormEvent<HTMLInputElement>) =>
            handleLinkChange((evt.target as HTMLInputElement).value)
          }
          className='!v2-border-none !px-2 !py-0 !h-10 v2-text-neutral-900'
          placeholder={getRequestLinkPlaceholder(request)}
        />
      </div>
    </div>
  );
}
